/**
 * @author NikolaV
 */

import * as React from 'react';
import { observer } from 'mobx-react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { ContextMenu } from '@shared/ui-react/ContextMenu';
import { ISavedViewsFiles } from '../SavedViewsPanelComponent';
import { DateUtils } from '@shared/utils/DateUtils';
import './PageStateItem.scss';
import { DateTimeSpan } from '@shared/ui-react/UtilComponents';
import { cs } from '@shared/utils/utils-classname';

@observer
export class PageStateItem extends React.Component<{
    item: IPageState;
    view?: 'thumbs' | 'list';
    isFocused?: boolean;
    onClick: (view: IPageState) => void;
    onEdit: (view: IPageState) => void;
    onDelete: (view: IPageState) => void;
    onDownload: (view: IPageState) => void;
    onDetails: (view: IPageState) => void;
    onShare: (view: IPageState) => void;
    onOpenInOtherMap: (view: IPageState) => void;
    noMenu?: boolean;
    models: ISavedViewsFiles;
    modelNames: Record<string, string>;
}> {
    rootEl: HTMLDivElement = null;

    componentDidMount() {
        setTimeout(() => {
            this.focus();
        }, 100);
    }

    componentDidUpdate(prevProps: any) {
        console.log('this.props.isFocused:', this.props.isFocused);
        if (this.props.isFocused !== prevProps.isFocused) {
            this.focus();
        }
    }

    focus() {
        if (this.props.isFocused) {
            this.rootEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
        }
    }

    render() {
        const { id, name, visibility, data, thumbnail, description, timestamp } = this.props.item;
        const thumbStyle = { backgroundImage: 'url("' + thumbnail + '")', backgroundSize: 'cover' };

        const Body =
            this.props.view === 'list' ? (
                <div className={cs('saved-views__item--list', { focused: this.props.isFocused })} ref={el => (this.rootEl = el)}>
                    <div className='saved-views__item--list__wrapper' onClick={() => this.props.onClick(this.props.item)} title={this.getFilesName(this.props.item)}>
                        <div className='saved-views__thumbnail' style={thumbStyle}></div>
                        <div className='saved-views__item__desc'>
                            {name && (
                                <strong title={name}>
                                    {name} {visibility === 'PRIVATE' && <i title={__('general.visibility_private')} className='iki iki-hide' />}
                                </strong>
                            )}
                            {description && <div title={description}>{description}</div>}
                            <DateTimeSpan timestamp={timestamp} />
                        </div>
                    </div>
                    {!this.props.noMenu && (
                        <Dropdown className='ik-button-small ik-button-secondary--subtle' closeOnBodyClick={true}>
                            <i className='iki iki-more' />
                            {this.renderMenu()}
                        </Dropdown>
                    )}
                </div>
            ) : (
                <div className={cs('saved-views__item--thumb', { focused: this.props.isFocused })} ref={el => (this.rootEl = el)}>
                    <div className='saved-views__thumbnail' style={thumbStyle} onClick={() => this.props.onClick(this.props.item)} title={this.getFilesName(this.props.item)}>
                        <div className='saved-views__title'>
                            {name && <strong>{name}</strong>}
                            {visibility === 'PRIVATE' && <i title={__('general.visibility_private')} className='iki iki-hide' />}
                        </div>
                    </div>

                    {!this.props.noMenu && (
                        <Dropdown className='ik-button-small ik-button-secondary--subtle' closeOnBodyClick={true}>
                            <i className='iki iki-more' />
                            {this.renderMenu()}
                        </Dropdown>
                    )}
                </div>
            );

        if (this.props.noMenu) {
            return Body;
        } else {
            return <ContextMenu render={this.renderMenu}>{Body}</ContextMenu>;
        }
    }

    getFilesName(item: IPageState) {
        let names: string = '';
        let namesOfDeleted: string = '';
        let counter: number = 0;
        if (item.data && item.data.panel) {
            if (item.data.panel.models) {
                item.data.panel.models.fileIds.forEach((id: number, i: number) => {
                    if (this.props.models[id] === undefined) {
                        if (this.props.modelNames && item.data.panel.models.headUuids && this.props.modelNames[item.data.panel.models.headUuids[i]]) {
                            names += ' - ' + this.props.modelNames[item.data.panel.models.headUuids[i]] + '\n';
                        } else {
                            counter++;
                            if (item.data.panel.models.fileNames) {
                                namesOfDeleted += ' - ' + item.data.panel.models.fileNames[i] + '\n';
                            }
                        }
                    } else {
                        names += ' - ' + this.props.models[id].name + '\n';
                    }
                });
                if (names != '') {
                    names += '\n';
                }
            }

            if (item.data.panel.bgMaps) {
                item.data.panel.bgMaps.fileIds.forEach((id: number, i: number) => {
                    if (this.props.models[id] === undefined) {
                        if (this.props.modelNames && item.data.panel.bgMaps.headUuids && this.props.modelNames[item.data.panel.bgMaps.headUuids[i]]) {
                            names += ' - ' + this.props.modelNames[item.data.panel.bgMaps.headUuids[i]] + '\n';
                        } else {
                            counter++;
                            if (item.data.panel.bgMaps.fileNames) {
                                namesOfDeleted += ' - ' + item.data.panel.bgMaps.fileNames[i] + '\n';
                            }
                        }
                    } else {
                        names += ' - ' + this.props.models[id].name + '\n';
                    }
                });
            }

            if (names != '' && namesOfDeleted != '') {
                names += '\n';
            }

            if (counter && namesOfDeleted == '') {
                names += counter === 1 ? kuura.getMessage('js_saved_views.not_showing_file') : kuura.getMessage('js_saved_views.not_showing_files', counter);
            } else if (namesOfDeleted !== '') {
                names += kuura.getMessage('js_saved_views.deleted_files') + ':\n' + namesOfDeleted;
            }
        }
        return `${__('general.name')}: ${item.name}\n${__('general.description')}: ${item.description ?? ''}\n${__('general.user')}: ${item.user?.username ?? ''}\n${__(
            'general.timestamp'
        )}: ${DateUtils.formatDateTime(item.timestamp)}\n\n${names}`;
    }

    renderMenu = () => {
        const props = { ...this.props };
        if (this.props.item.page === 'MAP3D_LEGACY') {
            delete props.onDetails;
            delete props.onShare;
            delete props.onOpenInOtherMap;
        }
        return <SavedViewItemMenu {...props} />;
    };
}

const SavedViewItemMenu = (props: any) => (
    <ul className='clickable'>
        {typeof props.onEdit === 'function' && <li onClick={() => props.onEdit(props.item)}>{__('general.edit')}</li>}
        {typeof props.onShare === 'function' && <li onClick={() => props.onShare(props.item)}>{__('general.share')}</li>}
        {typeof props.onDetails === 'function' && <li onClick={() => props.onDetails(props.item)}>{__('js_saved_views.partial_load')}</li>}
        {typeof props.onOpenInOtherMap === 'function' && (
            <li onClick={() => props.onOpenInOtherMap(props.item)}>{__('js_saved_views.open_in_[2d_or_3d]_map', window.location.href.includes('main.htm') ? '3D' : '2D')}</li>
        )}
        {typeof props.onDownload === 'function' && kuura.hasPrivilege('Download_Model') && <li onClick={() => props.onDownload(props.item)}>{__('general.download_files')}</li>}
        {typeof props.onDelete === 'function' && kuura.hasPrivilege('PROJECT_W') && (
            <li onClick={() => props.onDelete(props.item)} style={{ color: '#FF473D' }}>
                {__('general.delete')}
            </li>
        )}
    </ul>
);
